import { Alert, AlertIcon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ContributingEntityValidationIssueDto } from "../../../../autogen/bff-api";
import type { ContributionAudience } from "./ContributionWizard";

// t('Representative and company size must be filled in.')
// t('Every ground for exclusion must be answered.')
// t('Every qualification requirement must be answered.')
// t('You have declared a ground for exclusion. The buyer decides what it means.')
// t("You must confirm that the resources will be at the lead supplier's disposal.")
// t('The final declaration must be confirmed.')
// t('The answer has not been submitted yet.')
const LABEL_KEY: Record<ContributingEntityValidationIssueDto, string> = {
  [ContributingEntityValidationIssueDto.EntityInfoIncomplete]:
    "Representative and company size must be filled in.",
  [ContributingEntityValidationIssueDto.ExclusionGroundsIncomplete]:
    "Every ground for exclusion must be answered.",
  [ContributingEntityValidationIssueDto.QualificationAnswerMissing]:
    "Every qualification requirement must be answered.",
  [ContributingEntityValidationIssueDto.ExclusionGroundApplies]:
    "You have declared a ground for exclusion. The buyer decides what it means.",
  [ContributingEntityValidationIssueDto.CommitmentMissing]:
    "You must confirm that the resources will be at the lead supplier's disposal.",
  [ContributingEntityValidationIssueDto.FinalDeclarationMissing]:
    "The final declaration must be confirmed.",
  [ContributingEntityValidationIssueDto.ResponseNotSubmitted]:
    "The answer has not been submitted yet.",
};

// t('A ground for exclusion has been declared. The buyer decides what it means.')
// t('The company must confirm that the resources will be at your disposal.')
const LEAD_LABEL_KEY: Partial<Record<ContributingEntityValidationIssueDto, string>> = {
  [ContributingEntityValidationIssueDto.ExclusionGroundApplies]:
    "A ground for exclusion has been declared. The buyer decides what it means.",
  [ContributingEntityValidationIssueDto.CommitmentMissing]:
    "The company must confirm that the resources will be at your disposal.",
};

export const ContributionValidationIssues = ({
  issues,
  audience = "contributor",
}: {
  issues: ContributingEntityValidationIssueDto[];
  audience?: ContributionAudience;
}) => {
  const { t } = useTranslation();
  if (issues.length === 0) return null;

  const labelFor = (issue: ContributingEntityValidationIssueDto) =>
    (audience === "lead" ? LEAD_LABEL_KEY[issue] : undefined) ?? LABEL_KEY[issue];

  const declaredGround = issues.includes(ContributingEntityValidationIssueDto.ExclusionGroundApplies);
  const missing = issues.filter(
    (issue) => issue !== ContributingEntityValidationIssueDto.ExclusionGroundApplies,
  );

  return (
    <Stack spacing="3">
      {missing.length > 0 && (
        <Alert status="warning" borderRadius="lg" alignItems="flex-start">
          <AlertIcon />
          <Stack spacing="1">
            <Text fontSize="sm" fontWeight="semibold">
              {audience === "lead"
                ? t("This is still missing before the answer counts as complete:")
                : t("This is still missing before your answer counts as complete:")}
            </Text>
            {missing.map((issue) => (
              <Text key={issue} fontSize="sm">
                {t(labelFor(issue))}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}
      {declaredGround && (
        <Alert status="info" borderRadius="lg" alignItems="flex-start">
          <AlertIcon />
          <Text fontSize="sm">
            {t(labelFor(ContributingEntityValidationIssueDto.ExclusionGroundApplies))}
          </Text>
        </Alert>
      )}
    </Stack>
  );
};
